package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun AdminDashboardScreen(onNavigate: (String) -> Unit = {}) {
    val viewModel = koinViewModel<AdminViewModel>()
    val profile by viewModel.profile.collectAsState()
    val scrollState = rememberScrollState()

    LaunchedEffect(Unit) {
        viewModel.loadDashboardData()
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 20.dp, vertical = 14.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Box(
                                modifier = Modifier
                                    .size(36.dp)
                                    .clip(RoundedCornerShape(10.dp))
                                    .background(Brush.linearGradient(listOf(Color(0xFF1E293B), Color(0xFF0F172A)))),
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    imageVector = Icons.Outlined.AdminPanelSettings,
                                    contentDescription = null,
                                    tint = Color(0xFFFBBF24),
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                            Spacer(Modifier.width(12.dp))
                            Column {
                                Text(
                                    text = l10n("Sistem Yönetim Paneli"),
                                    fontSize = 18.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF0F172A)
                                )
                                Text(
                                    text = profile?.name?.ifBlank { null } ?: l10n("Otel ve Sistem Yapılandırması"),
                                    fontSize = 12.sp,
                                    color = Color(0xFF64748B)
                                )
                            }
                        }
                    }

                    LanguageSelectorRow(isDarkTheme = false)
                }
            }
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScrollbar(scrollState)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(scrollState)
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(24.dp)
            ) {
                // Section 1: Otel & Konaklama Yapılandırması
                AdminSection(
                    title = l10n("Otel & Konaklama Yapılandırması"),
                    description = l10n("Otel bilgileri, oda tipleri, envanter ve fiyat tarifeleri"),
                    items = listOf(
                        AdminMenuItem(
                            title = l10n("Otel Profili"),
                            subtitle = l10n("Kurumsal unvan, iletişim, vergi ve fatura bilgileri"),
                            icon = Icons.Outlined.Domain,
                            accentColor = Color(0xFF2563EB),
                            badge = profile?.companyTitle,
                            onClick = { onNavigate("HotelProfileScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Oda Yönetimi"),
                            subtitle = l10n("Oda numaraları, kat tanımları ve oda tipleri"),
                            icon = Icons.Outlined.MeetingRoom,
                            accentColor = Color(0xFF0D9488),
                            onClick = { onNavigate("RoomManagementScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Fiyat Periyotları & Tarifeler"),
                            subtitle = l10n("Sezonluk fiyatlar, hafta sonu farkları ve oda tarifeleri"),
                            icon = Icons.Outlined.PriceChange,
                            accentColor = Color(0xFFD97706),
                            onClick = { onNavigate("PricingManagementScreen") }
                        )
                    )
                )

                // Section 2: Menü, Acenteler & Entegrasyonlar
                AdminSection(
                    title = l10n("Satış, Menü & İş Ortakları"),
                    description = l10n("Restoran menüsü, acenteler, sözleşmeler ve kanal yönetimi"),
                    items = listOf(
                        AdminMenuItem(
                            title = l10n("Restoran & Menü Yönetimi"),
                            subtitle = l10n("Adisyon menüsü, ürün kategorileri ve oda servisi fiyatları"),
                            icon = Icons.Outlined.RestaurantMenu,
                            accentColor = Color(0xFFEA580C),
                            onClick = { onNavigate("AdminMenuManagementScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Acenta Yönetimi"),
                            subtitle = l10n("Acente kartları, komisyon oranları ve acente kayıtları"),
                            icon = Icons.Outlined.TravelExplore,
                            accentColor = Color(0xFF6366F1),
                            onClick = { onNavigate("AdminAgencyManagementScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Acente Kontenjan Sözleşmesi"),
                            subtitle = l10n("Acentelere özel oda blokajları ve dönemsel sözleşmeler"),
                            icon = Icons.Outlined.HistoryEdu,
                            accentColor = Color(0xFF8B5CF6),
                            onClick = { onNavigate("AgencyContractScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Tedarikçi Yönetimi"),
                            subtitle = l10n("Gıda, temizlik ve teknik malzeme tedarikçi kayıtları"),
                            icon = Icons.Outlined.LocalShipping,
                            accentColor = Color(0xFF0284C7),
                            onClick = { onNavigate("AdminSupplierManagementScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Channel Manager (OTA Senkronizasyonu)"),
                            subtitle = l10n("Booking, Expedia ve Airbnb çift yönlü senkronizasyon"),
                            icon = Icons.Outlined.SyncAlt,
                            accentColor = Color(0xFF059669),
                            badge = l10n("OTA Canlı"),
                            onClick = { onNavigate("ChannelManagerScreen") }
                        )
                    )
                )

                // Section 3: İnsan Kaynakları, Güvenlik & Raporlama
                AdminSection(
                    title = l10n("İK, Güvenlik & Sistem Raporları"),
                    description = l10n("Departmanlar, personel yetkileri, güvenlik ve genel istatistikler"),
                    items = listOf(
                        AdminMenuItem(
                            title = l10n("Departman Yönetimi"),
                            subtitle = l10n("Ön büro, kat hizmetleri, muhasebe ve mutfak hiyerarşisi"),
                            icon = Icons.Outlined.CorporateFare,
                            accentColor = Color(0xFF475569),
                            onClick = { onNavigate("AdminDepartmentManagementScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Personel & Yetki Yönetimi"),
                            subtitle = l10n("Personel hesapları, sistem rolleri ve erişim izinleri"),
                            icon = Icons.Outlined.Badge,
                            accentColor = Color(0xFF0891B2),
                            onClick = { onNavigate("AdminStaffManagementScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Üye Yönetimi & Kısıtlama"),
                            subtitle = l10n("Üye listesi, üyelik kodları ve anlık aktif/pasif kısıtlama"),
                            icon = Icons.Outlined.AdminPanelSettings,
                            accentColor = Color(0xFFD97706),
                            badge = l10n("Güvenlik"),
                            onClick = { onNavigate("AdminMembersScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Müşteri Kara Liste Yönetimi"),
                            subtitle = l10n("Sorunlu misafir kayıtları ve erken rezervasyon engelleme"),
                            icon = Icons.Outlined.Block,
                            accentColor = Color(0xFFDC2626),
                            badge = l10n("Güvenlik"),
                            onClick = { onNavigate("AdminBlacklistScreen") }
                        ),
                        AdminMenuItem(
                            title = l10n("Gelişmiş Raporlar & İstatistikler"),
                            subtitle = l10n("Doluluk trendleri, departman kârlılığı ve yönetici özetleri"),
                            icon = Icons.Outlined.Analytics,
                            accentColor = Color(0xFF10B981),
                            onClick = { onNavigate("StatisticsScreen") }
                        )
                    )
                )

                Spacer(Modifier.height(16.dp))
            }
        }
    }
}

private data class AdminMenuItem(
    val title: String,
    val subtitle: String,
    val icon: ImageVector,
    val accentColor: Color,
    val badge: String? = null,
    val onClick: () -> Unit
)

@Composable
private fun AdminSection(
    title: String,
    description: String,
    items: List<AdminMenuItem>
) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Column {
            Text(
                text = title,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF0F172A)
            )
            Text(
                text = description,
                fontSize = 12.sp,
                color = Color(0xFF64748B)
            )
        }

        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            items.forEach { item ->
                AdminCard(item = item)
            }
        }
    }
}

@Composable
private fun AdminCard(item: AdminMenuItem) {
    Card(
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier
            .fillMaxWidth()
            .clickable { item.onClick() }
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.weight(1f)
            ) {
                Box(
                    modifier = Modifier
                        .size(46.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .background(item.accentColor.copy(alpha = 0.12f)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = item.icon,
                        contentDescription = null,
                        tint = item.accentColor,
                        modifier = Modifier.size(24.dp)
                    )
                }

                Spacer(Modifier.width(14.dp))

                Column(modifier = Modifier.weight(1f)) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = item.title,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF0F172A)
                        )
                        if (item.badge != null) {
                            Surface(
                                color = item.accentColor.copy(alpha = 0.1f),
                                shape = CircleShape
                            ) {
                                Text(
                                    text = item.badge,
                                    fontSize = 10.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = item.accentColor,
                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                                )
                            }
                        }
                    }
                    Spacer(Modifier.height(3.dp))
                    Text(
                        text = item.subtitle,
                        fontSize = 12.sp,
                        color = Color(0xFF64748B),
                        lineHeight = 16.sp
                    )
                }
            }

            Spacer(Modifier.width(12.dp))

            Box(
                modifier = Modifier
                    .size(32.dp)
                    .clip(CircleShape)
                    .background(Color(0xFFF1F5F9)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.ArrowForward,
                    contentDescription = null,
                    tint = Color(0xFF64748B),
                    modifier = Modifier.size(16.dp)
                )
            }
        }
    }
}
